import {
  Id,
  Identifiable,
  resource as resourceMock,
  project as projectMock,
  list as listMock,
  task as taskMock,
  TimeSheet,
  TimeSheetFilter,
  Resource,
  Project,
  allocation,
  resource,
  PersonalTimeSheet,
  ResourceTypes,
} from "@planzone/dto";
import { HttpClient, HttpMethods } from "../types/httpClient";

type ResponseList = {
  uuid: Id;
  name: string;
  nbrComments: number;
  spentTimes: { [key: string]: number };
  nbrAllocatedHours: number;
  totalOfSpentTime: number;
  totalOfSpentTimeInWeek: number;
  totalOfRemainingTime: number;
  percentComplete: number;
  subActivities: ResponseList[];
  tasks: {
    uuid: Id;
    name: string;
    spentTimes: { [key: string]: number };
    nbrAllocatedHours: number;
    totalOfSpentTime: number;
    totalOfSpentTimeInWeek: number;
    totalOfRemainingTime: number;
    timeRecords: {
      uuid: Id;
      date: number;
      month: number;
      year: number;
      timeSpent: number;
      attributes?: { [key: string]: string };
      timeSpentInWeek: number;
    }[];
    taskResourceLatestRecord: string;
    taskResourceLatestRecordingDay: string;
    resourcePercentComplete: number;
    allocationData: {
      allocationUuid: string;
      resourceUuid: string;
    }[];
  }[];
};

type Response = {
  resourceUuid: Id;
  resourceName: string;
  profession: string;
  type: ResourceTypes;
  deactivated: boolean;
  integratedAt: string;
  timesheetDeadline: string;
  totalOfAllocatedHours: number;
  totalOfSpentTime: number;
  totalOfSpentTimeInWeek: number;
  totalOfRemainingTime: number;
  percentCompletion: number;
  spentTimes: { [key: string]: number };
  validatedSpentTimes: { [key: string]: number };
  unavailabilities: { [key: string]: boolean };
  projects: {
    projectUuid: string;
    projectName: string;
    integratedAt: string;
    deactivated: boolean;
    resourceRemoved?: boolean;
    totalOfAllocatedHours: number;
    totalOfSpentTime: number;
    totalOfSpentTimeInWeek: number;
    totalOfRemainingTime: number;
    spentTimes: { [key: string]: number };
  }[];
};

function isUnAvailable(
  at: string,
  unAvailabilities: { [key: string]: boolean },
): boolean {
  return unAvailabilities[at];
}

function responseToSpentTimes(
  res: { [key: string]: number },
  unAvailabilities: { [key: string]: boolean },
): { date: string; time: number; isAvailable: boolean }[] {
  return Object.entries(res)
    .map(([date, time]) => ({
      date,
      time,
      isAvailable: !isUnAvailable(date, unAvailabilities),
    }))
    .sort((first, second) => first.date.localeCompare(second.date));
}

function formatList(
  list: ResponseList,
  project: Identifiable<Project>,
  resource: Identifiable<Resource> & {
    unavailabilities: { [key: string]: boolean };
  },
): TimeSheet["resources"][number]["lists"][number] {
  return {
    ...listMock,
    ...list,
    percentComplete: list.percentComplete,
    project: { ...projectMock, ...project },
    subLists: list.subActivities.map((subList) =>
      formatList(subList, project, resource),
    ),
    spentTimes: responseToSpentTimes(
      list.spentTimes,
      resource.unavailabilities,
    ),
    tasks: list.tasks.map((task) => ({
      ...taskMock,
      ...task,
      spentTimes: responseToSpentTimes(
        task.spentTimes,
        resource.unavailabilities,
      ),
      timeRecords: task.timeRecords.map((record) => ({
        ...record,
        task: { ...taskMock, ...task },
        resource,
      })),
      allocations: task.allocationData.map((allocation) => ({
        uuid: allocation.allocationUuid,
        allocatedHours: task.nbrAllocatedHours,
        percentComplete: task.resourcePercentComplete,
        estimatedAt: 0,
        completedAt: "",
        resource: { ...resource, uuid: allocation.resourceUuid },
      })),
    })),
  };
}

function responseToTimeSheet(resource: Response): PersonalTimeSheet {
  return {
    spentTimes: responseToSpentTimes(
      resource.spentTimes,
      resource.unavailabilities,
    ),
    timesheetDeadline: resource.timesheetDeadline,
    resource: {
      ...resourceMock,
      ...resource,
      uuid: resource.resourceUuid,
      name: resource.resourceName,
      spentTimes: responseToSpentTimes(
        resource.spentTimes,
        resource.unavailabilities,
      ),
      validatedSpentTimes: responseToSpentTimes(
        resource.validatedSpentTimes,
        {},
      ),
    },
    projects: resource.projects.map((project) => ({
      ...projectMock,
      ...project,
      uuid: project.projectUuid,
      name: project.projectName,
      nbrAllocatedHours: project.totalOfAllocatedHours,
      spentTimes: responseToSpentTimes(
        project.spentTimes,
        resource.unavailabilities,
      ),
      memberDeactivated: project.deactivated,
      resourceRemoved: project.resourceRemoved ?? false,
    })),
  };
}

function getPersonalTimeSheet(httpClient: HttpClient) {
  return (
    resource: Identifiable<Resource>,
    { minDate, maxDate, ...filter }: TimeSheetFilter,
    page: number,
    size: number,
  ) =>
    httpClient
      .sendHttpRequest<Response>({
        endpoint: "/timesheets/personal/projects/paginated",
        method: HttpMethods.POST,
        data: {
          resourceUuid: resource.uuid,
          pageNumber: page,
          pageSize: size,
          startDate: minDate,
          endDate: maxDate,
          includeDeactivated: filter.includeDeactivated,
          includeRemovedFromProject: filter.includeRemovedFromProject,
          statuses: filter.projectStatuses
            ? filter.projectStatuses.map(({ uuid }) => uuid)
            : [],
          projects: filter.projects
            ? filter.projects.map(({ uuid }) => uuid)
            : [],
          tags: filter.projectsTags
            ? filter.projectsTags.map(({ name }) => name)
            : [],
          includeProjectsWithoutSpentTime:
            filter.includeProjectsWithoutSpentTime,
          includeProjectsWithoutSpentTimeInWeek:
            filter.includeProjectsWithoutSpentTimeInWeek,
          includeProjectsWithoutNonIntegratedSpentTimeInWeek:
            filter.includeProjectsWithoutNonIntegratedSpentTimeInWeek,
          projectPortfolios: filter.projectPortfolios
            ? filter.projectPortfolios.map(({ uuid }) => uuid)
            : [],
        },
      })
      .then(responseToTimeSheet);
}

export { getPersonalTimeSheet };
